import { useMemo, type ReactElement } from 'react'
import { CompanyInfo, TAlert, TButton, TDrawer, TeamList, TListItem } from '@components'
import { getLevelName, useProfile, useTeamListState, useTranslation } from '@front/model'
import { Stack, Group } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { isCompanyId, type ExternalLinkScopeId, isNotDefined } from '@terros/common'

type Props = {
  value: ExternalLinkScopeId
  onChange: (scopeId: ExternalLinkScopeId) => void
  error?: string
}

export const LinksScopeSelect = ({ value, onChange, error }: Props): ReactElement => {
  const { t } = useTranslation()
  const { company, isLoading: profileLoading, error: profileError } = useProfile()
  const { data: teams, isLoading: teamLoading, error: teamError, filtered } = useTeamListState()
  const [opened, { open, close }] = useDisclosure(false)

  const selectedScopeName = useMemo(() => {
    if (isCompanyId(value)) return company?.name
    const team = teams?.find((t) => t.teamId === value)
    return team?.level ? `${getLevelName(company, team.level)}: ${team.name}` : team?.name || value
  }, [value, teams, company])

  const handleScopeSelect = (scopeId: ExternalLinkScopeId): void => {
    onChange(scopeId)
    close()
  }

  if (profileLoading || teamLoading || isNotDefined(company)) {
    return <TButton id='scope' loading={profileLoading || teamLoading} />
  }

  if (error || profileError || teamError) {
    return <TAlert variant='error'>Error loading the profile</TAlert>
  }

  return (
    <Group>
      <Stack>
        <TButton variant='secondary' onClick={open}>
          {selectedScopeName}
        </TButton>
      </Stack>

      <TDrawer opened={opened} position='right' size='md' title={t('Select Scope')} onClose={close}>
        <Stack>
          <TListItem
            leftSection={<CompanyInfo companyId={company.companyId} linked={false} size='md' />}
            onClick={() => handleScopeSelect(company.companyId)}
          />
          <TeamList
            company={company}
            itemProps={{ hideArrow: true, onSelect: ({ teamId }) => handleScopeSelect(teamId) }}
            open={opened}
            teams={filtered}
          />
        </Stack>
      </TDrawer>
    </Group>
  )
}
